import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { useDispatch } from "react-redux";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { DDSLoader } from "three/examples/jsm/loaders/DDSLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { createFBXFileAction } from "../../store/actions/profile";
import { converBaseFileToBuffer, isBase64DataURL } from "../../helper/convertBase64";
import config from "../../config.json";


const FBXViewer = ({ fbxPath }) => {
 const dispatch = useDispatch();

  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Function to update dimensions
  const updateDimensions = () => {
    const container = document.getElementById("fbx-id");
    if (container) {
      setDimensions({
        width: container.clientWidth,
        height: container.clientHeight,
      });
    }
  };

  useEffect(() => {
    updateDimensions(); // Set initial dimensions
    window.addEventListener("resize", updateDimensions); // Listen for resize

    return () => {
      window.removeEventListener("resize", updateDimensions); // Cleanup on unmount
    };
  }, []);

  useEffect(async () => {
    if (!fbxPath || dimensions.width === 0 || dimensions.height === 0) return;

    initScene();

    if(isBase64DataURL(fbxPath)){
    const fbxImage = await converBaseFileToBuffer(fbxPath);

    // const fbxImage = await convertToFile(fbxPath, "fbxImage");
    const formData = new FormData();
    formData.append('fbxImage', fbxImage);
    dispatch(createFBXFileAction({ data: formData })).then(res => {
      const finalPath = config.LOCAL_API_URL + res.fbxFile;
      loadModel(finalPath);
    })
   }else{
    loadModel(fbxPath);
   }

    return () => cleanupScene(); // Cleanup on component unmount
  }, [fbxPath, dimensions]);

  const initScene = () => {
    if (!mountRef.current) return;

    while (mountRef.current.firstChild) {
      mountRef.current.removeChild(mountRef.current.firstChild);
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xeeeeee);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      50,
      dimensions.width / dimensions.height,
      0.1,
      1000
    );
    camera.position.set(0, 0, 5);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(dimensions.width, dimensions.height);
    rendererRef.current = renderer;
    mountRef.current.appendChild(renderer.domElement);

    const light = new THREE.DirectionalLight(0xffffff, 1);
    light.position.set(5, 10, 5);
    scene.add(light);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.enableZoom = true;
    controls.target.set(0, 0, 0);
    controls.update();

    animate(controls);
  };

  const loadModel = (url) => {
    if (!url) return;

    // Before loading FBX
    const ddsLoader = new DDSLoader();
    FBXLoader.prototype.setDDSLoader = function (ddsLoader) {
      this.ddsLoader = ddsLoader;
    };

    // Create the FBX loader and attach the DDSLoader
    const loader = new FBXLoader();
    loader.setDDSLoader(ddsLoader);

    loader.load(
      url,
      (object) => {
        // object.scale.set(1, 1, 1);
        object.scale.set(0.01, 0.01, 0.01); // Try small scale values
        object.position.set(0, 0, 0);
        object.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });

        const box = new THREE.Box3().setFromObject(object);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        object.position.sub(center);

        const maxSize = Math.max(size.x, size.y, size.z);
        const fitHeightDistance =
          maxSize / (2 * Math.tan((cameraRef.current.fov * Math.PI) / 360));
        const fitWidthDistance = fitHeightDistance / cameraRef.current.aspect;
        const distance = Math.max(fitHeightDistance, fitWidthDistance);
        cameraRef.current.position.set(center.x, center.y, center.z + distance * 1.5);
        cameraRef.current.lookAt(center);

        sceneRef.current.add(object);
      },
      (xhr) => {
        console.log(xhr.loaded, `Loading FBX: ${((xhr.loaded / xhr.total) * 100).toFixed(2)}%`, xhr.total);
      },
      (error) => {
        console.error("Error loading FBX:", error);
      }
    );
  };

  const cleanupScene = () => {
    if (rendererRef.current) {
      rendererRef.current.dispose();
    }
  };

  const animate = (controls) => {
    const loop = () => {
      requestAnimationFrame(loop);
      controls.update();
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };
    loop();
  };

  return (
    <div ref={mountRef} style={{ width: dimensions.width, height: dimensions.height }} />
  );
};

export default FBXViewer;
